import React from 'react';
import styled from 'styled-components';

// Tiny visual badges that surface signals already in the item payload.
// Cheap to render, valuable to the buyer's scan.

const TopLeftStack = styled.div`
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  z-index: 4;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  background: ${(p) => p.$bg || 'rgba(0,0,0,0.7)'};
  color: ${(p) => p.$color || 'white'};
`;

const TrustRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.5rem;
`;

const TrustChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  background: ${(p) => p.$bg || '#f0f4ff'};
  color: ${(p) => p.$color || '#3949ab'};
  border: 1px solid ${(p) => p.$border || 'rgba(57, 73, 171, 0.15)'};
`;

const isWithinHours = (dateString, hours) => {
  if (!dateString) return false;
  const t = new Date(dateString).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t < hours * 3600 * 1000;
};

export const ItemCornerBadges = ({ item }) => {
  const isNew = isWithinHours(item.created_at, 48);
  const isLowStock = typeof item.in_stock === 'number' && item.in_stock > 0 && item.in_stock <= 2;
  const isTopSeller = typeof item.seller_aura === 'number' && item.seller_aura >= 4.5;
  const distance = typeof item.distance_km === 'number' ? item.distance_km : null;

  return (
    <TopLeftStack>
      {isNew && <Pill $bg="#10b981">NEW</Pill>}
      {isLowStock && (
        <Pill $bg="#fef3c7" $color="#92400e">
          {item.in_stock === 1 ? 'Last 1' : `Only ${item.in_stock} left`}
        </Pill>
      )}
      {isTopSeller && (
        <Pill $bg="#eef2ff" $color="#3949ab">
          ⭐ Top seller
        </Pill>
      )}
      {distance !== null && (
        <Pill $bg="rgba(255,255,255,0.95)" $color="#1f2937">
          📍 {distance < 1 ? '< 1' : distance.toFixed(1)} km
        </Pill>
      )}
    </TopLeftStack>
  );
};

export const ItemTrustStrip = ({ item }) => {
  const chips = [];
  if (item.approval_status === 'APPROVED') {
    chips.push({ key: 'approved', label: '✓ Approved', bg: '#ecfdf5', color: '#047857', border: 'rgba(4,120,87,0.15)' });
  }
  // Authenticity check is supported (we have a model for it). Even though the
  // serializer doesn't surface availability today, we leave the slot — a
  // future enhancement can flip a boolean `authenticity_available`.
  if (item.authenticity_available) {
    chips.push({ key: 'auth', label: '🛡️ Authentic', bg: '#f5f3ff', color: '#5b21b6', border: 'rgba(91,33,182,0.15)' });
  }
  if (chips.length === 0) return null;
  return (
    <TrustRow>
      {chips.map((c) => (
        <TrustChip key={c.key} $bg={c.bg} $color={c.color} $border={c.border}>
          {c.label}
        </TrustChip>
      ))}
    </TrustRow>
  );
};

export default ItemCornerBadges;
